import { useStateMachine } from '@heidi/flow/react';
import { useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';

import {
  chronicleConnectionContextAtom,
  chronicleDeviceStateAtom,
  manualReconnectPendingAtom,
  resetChronicleConnectionContextAtom,
  resetChronicleDeviceStateAtom,
  resetChronicleSessionAtom,
} from '@/atoms/chronicle';
import { listHardwareDevices } from '@/pages/api/scribeAPI';
import { chronicleBridge } from '@/tauri/chronicle';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';
import { isWindows } from '@heidi/utils';

import {
  chronicleStartupAutoConnectMachine,
  type ChronicleStartupDevice,
} from './chronicle-startup-auto-connect.machine';
import { assertChronicleBlePrereqs } from './utils/assertChronicleBlePrereqs';
import {
  getOrGenerateBindingCode,
  pushSignedInChronicleUser,
  syncBindingCodeToServer,
} from './utils/bindingCode';

class NoLinkedDeviceError extends Error {
  constructor() {
    super('No linked device');
    this.name = 'NoLinkedDeviceError';
  }
}

/** Distinct from NoLinkedDeviceError so BLE prereq aborts are not mislabeled. */
class ChronicleBlePrereqError extends Error {
  constructor(code: string) {
    super(code);
    this.name = 'ChronicleBlePrereqError';
  }
}

interface UseChronicleStartupAutoConnectOptions {
  enabled?: boolean;
  resetOnDisabled?: boolean;
  /**
   * When the hook is disabled because auth settled to logged-out,
   * tear down the BLE transport and disarm park/watcher reconnect arms.
   *
   * Must NOT be tied to the Chronicle feature flag — flags can hydrate after
   * logout, and a flag-only disable must not disconnect a still-authenticated
   * session.
   */
  teardownTransportOnSettledLogout?: boolean;
}

/**
 * Restores linked device state and triggers BLE auto-connect on app startup.
 *
 * Mirrors iOS `MainAppViewModel.connectToLinkedDevice()`:
 * 1. Fetch linked devices from backend
 * 2. Populate Jotai atoms with device info
 * 3. Resolve binding code from server (APP-4359: server-authoritative)
 * 4. Trigger Rust auto-connect with the server-fetched code
 *
 * Best-effort — failures are logged but do not block app startup.
 * Mount this hook once in ChronicleStateProvider.
 */
export function useChronicleStartupAutoConnect({
  enabled = true,
  resetOnDisabled = true,
  teardownTransportOnSettledLogout = false,
}: UseChronicleStartupAutoConnectOptions = {}) {
  const setConnectionContext = useSetAtom(chronicleConnectionContextAtom);
  const setDeviceState = useSetAtom(chronicleDeviceStateAtom);
  const resetDeviceState = useSetAtom(resetChronicleDeviceStateAtom);
  const resetConnectionContext = useSetAtom(resetChronicleConnectionContextAtom);
  const resetSession = useSetAtom(resetChronicleSessionAtom);
  const setManualReconnectPending = useSetAtom(manualReconnectPendingAtom);
  const hasStartedRef = useRef(false);
  const hasTornDownTransportRef = useRef(false);

  const populateDeviceAtoms = (device: ChronicleStartupDevice) => {
    setConnectionContext((prev) => ({
      ...prev,
      linkedDeviceId: device.id,
    }));
    setDeviceState((prev) => ({
      ...prev,
      deviceName: device.deviceName ?? prev.deviceName,
      // Backend hardware_device lags OTA; live BLE APP&FW in prev wins on cold start.
      firmwareVersion: prev.firmwareVersion ?? device.firmwareVersion ?? null,
      serialNumber: device.serialNumber ?? prev.serialNumber ?? null,
    }));
  };

  const [startupState, sendStartup] = useStateMachine(
    chronicleStartupAutoConnectMachine,
    {
      services: {
        fetchDevices: async (_context, { signal }) => {
          try {
            const response = await listHardwareDevices();
            if (signal.aborted) throw new NoLinkedDeviceError();
            const raw = response.data?.[0];
            if (!raw) throw new NoLinkedDeviceError();

            const device: ChronicleStartupDevice = {
              id: raw.id,
              deviceName: raw.device_name ?? null,
              firmwareVersion: raw.firmware_version ?? null,
              serialNumber: raw.serial_number ?? null,
            };
            populateDeviceAtoms(device);
            return device;
          } catch (error) {
            if (error instanceof NoLinkedDeviceError) throw error;
            logger.debug({
              msg: 'Chronicle startup auto-connect skipped',
              error: error instanceof Error ? error.message : String(error),
            });
            throw error;
          }
        },
        resolveBindingCode: async (_context, { signal }) => {
          try {
            const binding = await getOrGenerateBindingCode();
            if (signal.aborted) return undefined;
            if (!binding.isNew) {
              return binding.code;
            }
          } catch (error) {
            logger.warn({
              msg: 'Binding code fetch failed before auto-connect, Rust will use the stored code',
              error: error instanceof Error ? error.message : String(error),
            });
          }
          return undefined;
        },
        connect: async (context, { signal }) => {
          const device = context.device;
          if (!device) throw new NoLinkedDeviceError();

          try {
            // Startup BLE bar differs from settings reconnect / DeviceSearch:
            // hard-stop on permission denied everywhere. Radio-off
            // (BLUETOOTH_DISABLED) falls through on Windows only so Rust can
            // arm the persistent watcher (watcher_startup_connect) — nothing
            // else arms it on cold-start with BT off. On macOS, hard-stop:
            // park_startup_connect wait_powered_on would timeout into
            // NATIVE_BLE_NOT_POWERED_ON → Disconnected + UNKNOWN and leave
            // the park unarmed (HAR-1910 Soft-PASS / Jojo). Bridge UNKNOWN
            // is not a radio prereq — hard-stop separately.
            const blePrereqs = await assertChronicleBlePrereqs();
            if (!blePrereqs.ok) {
              const code = blePrereqs.error.code;
              const windowsRadioOffFallThrough =
                code === 'BLUETOOTH_DISABLED' && isWindows;
              if (!windowsRadioOffFallThrough) {
                logger.debug({
                  msg:
                    code === 'UNKNOWN'
                      ? 'Chronicle startup auto-connect skipped — BLE status unknown'
                      : 'Chronicle startup auto-connect skipped — BLE prereqs not met',
                  code,
                });
                throw new ChronicleBlePrereqError(code);
              }
              logger.debug({
                msg: 'Chronicle startup auto-connect continuing despite BLE radio prereq — Rust will gate/reconnect',
                code,
              });
            }
            if (signal.aborted) throw new NoLinkedDeviceError();

            // Rust resolves the binding code and reconnect target from the
            // signed-in account's stored identity, and drops identity writes
            // it cannot attribute. The bulk-sync provider pushes it too, but
            // that effect is not ordered against this service — await it here.
            // Connecting with no account resolved would store neither the
            // confirmed code nor the reconnect target.
            if (!(await pushSignedInChronicleUser())) {
              logger.warn({
                msg: 'Chronicle startup auto-connect skipped — no signed-in account to attribute the connection to',
              });
              throw new NoLinkedDeviceError();
            }
            if (signal.aborted) throw new NoLinkedDeviceError();

            const { confirmedBindingCode } = await chronicleBridge.ensureConnectedIdle({
              bindingCode: context.bindingCode,
              // BLE connect hint is the advertised device name, not serial_number.
              deviceName: device.deviceName ?? undefined,
            });
            if (signal.aborted) throw new NoLinkedDeviceError();
            return confirmedBindingCode;
          } catch (error) {
            logger.debug({
              msg: 'Chronicle startup auto-connect skipped',
              error: error instanceof Error ? error.message : String(error),
            });
            throw error;
          }
        },
        syncBindingCode: (context, { signal }) => {
          if (signal.aborted || !context.confirmedBindingCode) return;
          void syncBindingCodeToServer(context.confirmedBindingCode, {
            connectedSerial:
              context.device?.serialNumber ?? context.device?.deviceName ?? null,
          });
        },
      },
    }
  );

  useEffect(() => {
    if (!enabled) {
      // Settled logout must release park/watcher arms + BLE link so
      // the Remote is free on the login screen. `disconnect()` already stops
      // the DeviceMonitor, cancels parked connect, and disarms the watcher —
      // including when already connected (unlike USB `StopReconnection`).
      if (teardownTransportOnSettledLogout && !hasTornDownTransportRef.current) {
        hasTornDownTransportRef.current = true;
        if (chronicleBridge.isAvailable()) {
          // Best-effort: release park/watcher arms + drop the BLE link. Atom
          // reset below clears FE state; unexpected-disconnect toasts are
          // gated on linkedDeviceId which the reset nulls.
          void chronicleBridge.disconnect().catch((error) => {
            logger.warn({
              msg: 'Chronicle logout transport teardown failed',
              error: error instanceof Error ? error.message : String(error),
            });
          });
        }
      }

      if (resetOnDisabled) {
        hasStartedRef.current = false;
        resetDeviceState();
        resetConnectionContext();
        resetSession();
        setManualReconnectPending(false);
        if (!startupState.matches('idle')) {
          sendStartup('RESET');
        }
      } else if (!startupState.matches('idle') && !startupState.matches('done')) {
        sendStartup('RESET');
      }
      return;
    }

    // Re-arm teardown for the next logout after a successful enable cycle.
    hasTornDownTransportRef.current = false;

    if (!chronicleBridge.isAvailable()) return;
    if (hasStartedRef.current) return;
    if (!startupState.matches('idle')) {
      if (startupState.matches('done')) {
        sendStartup('RESET');
      }
      return;
    }

    hasStartedRef.current = true;
    sendStartup({ type: 'START' });
  }, [
    enabled,
    resetOnDisabled,
    teardownTransportOnSettledLogout,
    resetDeviceState,
    resetConnectionContext,
    resetSession,
    setManualReconnectPending,
    sendStartup,
    startupState,
  ]);
}
